import { useState } from 'react';
import Modal from '@/components/base/Modal';
import { BACKUP_TABLES, exportDatabaseBackup, BackupResult } from '@/lib/backup';

interface BackupModalProps {
  open: boolean;
  onClose: () => void;
}

type Status = 'idle' | 'loading' | 'success' | 'error';

export default function BackupModal({ open, onClose }: BackupModalProps) {
  const [status, setStatus] = useState<Status>('idle');
  const [percent, setPercent] = useState(0);
  const [message, setMessage] = useState('');
  const [result, setResult] = useState<BackupResult | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  function reset() {
    setStatus('idle');
    setPercent(0);
    setMessage('');
    setResult(null);
    setErrorMessage('');
  }

  function handleClose() {
    if (status === 'loading') return;
    reset();
    onClose();
  }

  async function handleBackup() {
    setStatus('loading');
    setPercent(2);
    setMessage('Iniciando respaldo…');
    setResult(null);
    setErrorMessage('');

    try {
      const response = await exportDatabaseBackup((progress) => {
        setPercent(progress.percent);
        setMessage(progress.message);
      });
      setResult(response);
      setStatus('success');
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'No se pudo generar el respaldo. Intenta de nuevo.',
      );
      setStatus('error');
    }
  }

  return (
    <Modal open={open} onClose={handleClose} title="Respaldo de Base de Datos" size="md">
      <div className="flex flex-col gap-5">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
            <i className="ri-database-2-line text-primary-700 text-2xl"></i>
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-foreground-900">Copia de seguridad completa del sistema</p>
            <p className="text-sm text-foreground-600 mt-1 leading-relaxed">
              Descarga un respaldo completo (Clientes, Suscripciones, Facturas, Catálogo y
              Contactos) en formato ZIP. Incluye cada tabla en JSON estructurado y CSV listo
              para hoja de cálculo.
            </p>
          </div>
        </div>

        <div className="rounded-xl bg-background-100/80 border border-background-200 p-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500 mb-3">
            Contenido del respaldo
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {BACKUP_TABLES.map((config) => (
              <li key={config.table} className="flex items-center gap-2 text-xs text-foreground-700">
                <i className="ri-checkbox-blank-circle-fill text-[6px] text-primary-500"></i>
                <span className="truncate">{config.label}</span>
              </li>
            ))}
          </ul>
        </div>

        {status === 'loading' && (
          <div className="rounded-xl border border-primary-200 bg-primary-50/70 p-4">
            <div className="flex items-center gap-3">
              <i className="ri-loader-4-line animate-spin text-primary-600 text-lg"></i>
              <span className="text-xs font-medium text-primary-800 flex-1 truncate">{message}</span>
              <span className="text-xs font-bold text-primary-700">{percent}%</span>
            </div>
            <div className="mt-3 h-1.5 w-full rounded-full bg-primary-100 overflow-hidden">
              <div
                className="h-full rounded-full bg-primary-500 transition-all duration-300"
                style={{ width: `${Math.min(100, Math.max(2, percent))}%` }}
              ></div>
            </div>
          </div>
        )}

        {status === 'success' && result && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <div className="flex items-center gap-2.5">
              <i className="ri-checkbox-circle-fill text-emerald-600 text-lg"></i>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-emerald-800">Respaldo descargado correctamente</p>
                <p className="text-[11px] text-emerald-700 truncate">{result.filename}</p>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
              {Object.entries(result.recordCounts).map(([table, count]) => {
                const label = BACKUP_TABLES.find((item) => item.table === table)?.label || table;
                return (
                  <span key={table} className="text-[11px] text-emerald-800">
                    {label}: <strong className="font-semibold">{count}</strong>
                  </span>
                );
              })}
            </div>
            {result.failedTables.length > 0 && (
              <p className="mt-2 text-[11px] text-amber-700">
                Sin acceso en esta corrida: {result.failedTables.join(', ')}. El resto se respaldó completo.
              </p>
            )}
          </div>
        )}

        {status === 'error' && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex items-start gap-2.5">
            <i className="ri-error-warning-fill text-red-600 text-lg mt-0.5"></i>
            <div>
              <p className="text-xs font-semibold text-red-800">No se pudo generar el respaldo</p>
              <p className="text-[11px] text-red-700 mt-0.5 break-words">{errorMessage}</p>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:justify-end gap-2.5 pt-1">
          <button
            type="button"
            onClick={handleClose}
            disabled={status === 'loading'}
            className="px-4 py-2.5 rounded-md bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium cursor-pointer transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {status === 'success' ? 'Cerrar' : 'Cancelar'}
          </button>
          <button
            type="button"
            onClick={handleBackup}
            disabled={status === 'loading'}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {status === 'loading' ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Generando respaldo…
              </>
            ) : (
              <>
                <i className="ri-download-2-line"></i>
                {status === 'success' ? 'Descargar de nuevo (.ZIP)' : 'Descargar Respaldo Completo (.ZIP)'}
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
}